import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import Seo from "@/components/site/Seo";
import { PORTFOLIO } from "@/data/site";
import { beautySalonSchema, breadcrumb } from "@/data/schema";

const AUTOPLAY_MS = 3800;

export default function Gallery() {
  const n = PORTFOLIO.length;
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  const go = useCallback((d) => setIndex((v) => (v + d + n) % n), [n]);

  useEffect(() => {
    if (lightbox) return;
    const t = setInterval(() => setIndex((v) => (v + 1) % n), AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [lightbox, n]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, go]);

  return (
    <>
      <Seo
        title="Gallery | Golden Touch Lash & Brow, Vero Beach FL"
        description="Browse real client work from Golden Touch Lash & Brow - lash extensions, brows, microshading and beauty results in Vero Beach, Florida."
        path="/gallery"
        jsonLd={[
          beautySalonSchema(),
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Gallery", path: "/gallery" },
          ]),
        ]}
      />

      <section className="bg-onyx pt-28 md:pt-32 pb-6 text-center">
        <div className="mx-auto max-w-3xl px-6">
          <p className="eyebrow">My Work</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl text-ivory">The Golden Touch</h1>
        </div>
      </section>

      {/* Large auto-rotating stage */}
      <section className="bg-onyx pb-10" aria-label="Work gallery">
        <div
          className="relative mx-auto max-w-6xl px-4 sm:px-6"
        >
          <div
            className="relative h-[62vh] min-h-[420px] md:h-[78vh] overflow-hidden rounded-2xl bg-charcoal"
            style={{ background: "radial-gradient(60% 70% at 50% 40%, rgba(128,114,96,0.18) 0%, rgba(10,10,10,1) 72%)" }}
          >
            {PORTFOLIO.map((item, i) => (
              <img
                key={i}
                src={item.src}
                alt={`Golden Touch lash and brow work ${i + 1}`}
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
                onClick={() => setLightbox(true)}
                className={`absolute inset-0 h-full w-full cursor-zoom-in object-contain transition-all duration-[900ms] ease-out ${
                  i === index ? "opacity-100 scale-100" : "opacity-0 scale-[1.03] pointer-events-none"
                }`}
              />
            ))}

            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous photo"
              data-testid="gallery-prev"
              className="absolute left-3 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 bg-onyx/60 text-gold backdrop-blur-sm transition-colors hover:bg-gold hover:text-onyx"
            >
              <ChevronLeft size={26} />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next photo"
              data-testid="gallery-next"
              className="absolute right-3 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 bg-onyx/60 text-gold backdrop-blur-sm transition-colors hover:bg-gold hover:text-onyx"
            >
              <ChevronRight size={26} />
            </button>

            <div className="absolute bottom-4 right-5 rounded-full bg-onyx/70 px-3 py-1 text-xs tracking-[0.2em] text-gold backdrop-blur-sm">
              {index + 1} / {n}
            </div>
          </div>

          {/* Thumbnail strip */}
          <div className="mt-5 flex gap-2.5 overflow-x-auto pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {PORTFOLIO.map((item, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Show photo ${i + 1}`}
                className={`relative h-16 w-14 shrink-0 overflow-hidden rounded-lg sm:h-20 sm:w-16 transition-all duration-300 ${
                  i === index ? "ring-2 ring-gold opacity-100" : "opacity-50 hover:opacity-90"
                }`}
              >
                <img src={item.src} alt="" loading="lazy" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {lightbox && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-onyx/95 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Gallery image viewer"
          onClick={() => setLightbox(false)}
        >
          <button
            type="button"
            onClick={() => setLightbox(false)}
            aria-label="Close"
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center text-ivory hover:text-gold"
          >
            <X size={28} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            aria-label="Previous image"
            className="absolute left-3 flex h-12 w-12 items-center justify-center text-ivory hover:text-gold md:left-8"
          >
            <ChevronLeft size={40} />
          </button>
          <img
            src={PORTFOLIO[index].src}
            alt={`Golden Touch work ${index + 1}`}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[86vh] w-auto max-w-full object-contain"
          />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            aria-label="Next image"
            className="absolute right-3 flex h-12 w-12 items-center justify-center text-ivory hover:text-gold md:right-8"
          >
            <ChevronRight size={40} />
          </button>
        </div>
      )}
    </>
  );
}
